/**
 * Dispose board logic (alignment map V1-F7-B1c Dispose; F7-B1.c): one disposition per held item,
 * totals that must equal what is held, the basis (a failing confirming report, or a waiver in
 * writing) and the signature. Sacks: Red tie (ordinary, DIVERT) or Destroy. A pack lot: Destroy
 * only, with its pouch count. Mirrors uc-core DispositionDto and dispositionQtyProblem.
 */
export type DisposeChoice = 'DIVERT' | 'DESTROY' | ''

export interface DisposeRow {
  unitId: string
  code: string
  isPack: boolean
  /** Kilos still held (sacks) or the pack lot's kilos. */
  heldG: number | null
  /** Pouches still at the hall (pack lots). */
  pouchesAtHall: number | null
  choice: DisposeChoice
  kg: string
  pouches: string
  /** Already disposed (status DESTROYED / stream CONV): shown, not signed again. */
  done: boolean
}

export const WAIVER_NOTE_MIN = 20
export const REASON_MIN = 5

const KG = /^\d+(\.\d{1,3})?$/

export function kgStringToGrams(kg: string): number | null {
  const t = kg.trim()
  if (!KG.test(t)) return null
  const [w, f = ''] = t.split('.')
  return Number(w) * 1000 + Number(f.padEnd(3, '0'))
}

/** A row's own problem (null = fine). */
export function rowProblem(r: DisposeRow): string | null {
  if (r.done) return null
  if (!r.choice) return 'Choose a disposition'
  if (r.isPack) {
    if (r.choice !== 'DESTROY') return 'A pack lot can only be destroyed'
    const n = Number(r.pouches)
    if (!Number.isInteger(n) || r.pouches.trim() === '') return 'Type the pouch count'
    if (r.pouchesAtHall !== null && n !== r.pouchesAtHall)
      return `Pouches must equal the ${r.pouchesAtHall} still at the hall`
  }
  const g = kgStringToGrams(r.kg)
  if (g === null || g <= 0) return 'Type the kilos (up to 3 decimals)'
  if (!r.isPack && r.heldG !== null && g > r.heldG) return 'More kilos than are held'
  return null
}

export function disposeTotals(rows: ReadonlyArray<DisposeRow>) {
  const open = rows.filter((r) => !r.done)
  const kgG = open.reduce((s, r) => s + (kgStringToGrams(r.kg) ?? 0), 0)
  const heldG = open.reduce((s, r) => s + (r.heldG ?? 0), 0)
  const pouches = open.filter((r) => r.isPack).reduce((s, r) => s + (Number(r.pouches) || 0), 0)
  const pouchesHeld = open.filter((r) => r.isPack).reduce((s, r) => s + (r.pouchesAtHall ?? 0), 0)
  return {
    kgG,
    heldG,
    kgMatches: kgG === heldG,
    pouches,
    pouchesHeld,
    pouchesMatch: pouches === pouchesHeld,
  }
}

export interface DisposeBasis {
  basis: 'RETEST' | 'WAIVED' | ''
  confirmingReportId: string
  waiverNote: string
}

/** Why the disposition cannot be signed yet (null = it can). */
export function disposeBlocked(
  rows: ReadonlyArray<DisposeRow>,
  b: DisposeBasis,
  reason: string,
  password: string,
): string | null {
  const open = rows.filter((r) => !r.done)
  if (open.length === 0) return 'Everything held is already disposed of.'
  if (!b.basis) return 'Choose the basis and a disposition for each row first.'
  if (b.basis === 'RETEST' && !b.confirmingReportId) return 'Pick the confirming report.'
  if (b.basis === 'WAIVED' && b.waiverNote.trim().length < WAIVER_NOTE_MIN)
    return `Write the waiver (at least ${WAIVER_NOTE_MIN} letters).`
  const bad = open.find((r) => rowProblem(r) !== null)
  if (bad) return `${bad.code}: ${rowProblem(bad)}`
  if (reason.trim().length < REASON_MIN) return 'Write why, in one or two lines.'
  if (!password) return 'Enter your password.'
  return null
}

/** The request body for POST /units/:id/dispositions (without the reauth token). */
export function dispositionBody(r: DisposeRow, b: DisposeBasis, reason: string) {
  return {
    decision: r.choice as 'DIVERT' | 'DESTROY',
    qtyKg: r.kg.trim(),
    ...(r.isPack ? { pouches: Number(r.pouches) } : {}),
    basis: b.basis as 'RETEST' | 'WAIVED',
    ...(b.basis === 'RETEST'
      ? { confirmingReportId: b.confirmingReportId }
      : { waiverNote: b.waiverNote.trim() }),
    reason: reason.trim(),
  }
}
